import { FormProvider, useForm } from "react-hook-form";
import {
  useNavigate,
  useSearchParams,
  useLocation,
  Link,
} from "react-router-dom";
import type { OrderFormValues } from "../../types/forms";
import OrderDetails from "./sections/orderDetails";
import ContactInformation from "./sections/contactInformation";
import PartInformation from "./sections/partInformation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getOrderById, saveOrder } from "../../api/orderService";
import { useEffect, useRef, useState } from "react";
import LoadingScreen from "../../components/loadingScreen";
import { cleanPayload } from "../../utils/cleanPayload";
import { ANALYTICS_EVENTS, trackEvent } from "../../lib/analytics";
import { routeEndpoints } from "../../routes/routeEndpoints";
import DiscardOrderModal from "../../components/modals/DiscardOrderModal";
import ShippingStatusModal from "../../components/modals/ShippingStatusModal";
import OrderSubmittedModal from "../../components/modals/OrderSubmittedModal";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useAuth } from "@clerk/clerk-react";

export default function OrderPage() {
  const methods = useForm<OrderFormValues>();
  const {
    reset,
    formState: { isDirty },
  } = methods;
  const { isLoaded, isSignedIn } = useAuth();
  const isAuthenticated = isLoaded && !!isSignedIn;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const pageEntryTime = useRef<number>(0);
  const didSubmit = useRef(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
  const [isSubmittedModalOpen, setIsSubmittedModalOpen] = useState(false);

  useEffect(() => {
    pageEntryTime.current = Date.now();
    return () => {
      const timeSpentSeconds = Math.round(
        (Date.now() - pageEntryTime.current) / 1000,
      );
      if (!didSubmit.current) {
        trackEvent(ANALYTICS_EVENTS.ORDER_PAGE_DROP_OFF, {
          time_on_page_seconds: timeSpentSeconds,
        });
      }
    };
  }, []);

  const orderId = searchParams.get("id");

  const { data, isLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => getOrderById(orderId!),
    enabled: !!orderId,
  });

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        addressInfo: {
          isReturnAddressDifferent:
            data.addressInfo?.isReturnAddressDifferent ?? false,
          shippingInfo: data.addressInfo?.shippingInfo ?? undefined,
        },
      });
    }
  }, [data, reset]);

  const { mutate: saveOrderMutation, isPending } = useMutation({
    mutationFn: async ({
      payload,
      imageFiles,
    }: {
      payload: Record<string, unknown>;
      imageFiles?: Record<string, File>;
    }) => {
      await saveOrder(payload, imageFiles);
    },
    onSuccess: () => {
      const timeSpentSeconds = Math.round(
        (Date.now() - pageEntryTime.current) / 1000,
      );
      didSubmit.current = true;
      trackEvent(ANALYTICS_EVENTS.ORDER_SUBMITTED, {
        time_on_page_seconds: timeSpentSeconds,
      });
      if (isAuthenticated) {
        setIsShippingModalOpen(true);
      } else {
        navigate(`${routeEndpoints.shippingStatus}?id=${orderId}`);
      }
    },
  });

  const onSubmit = (formData: OrderFormValues) => {
    const { partImageFile, ...restFormData } = formData;

    const rawPayload = {
      ...restFormData,
      orderId,
      addressInfo: restFormData.addressInfo
        ? {
            ...restFormData.addressInfo,
            shippingInfo: restFormData.addressInfo.isReturnAddressDifferent
              ? restFormData.addressInfo.shippingInfo
              : null,
          }
        : undefined,
    };

    const payload = cleanPayload(rawPayload) as Record<string, unknown>;
    const targetLineItemId = restFormData.orderLineItems?.[0]?.orderLineItemId;
    const imageFiles =
      partImageFile && targetLineItemId
        ? { [targetLineItemId]: partImageFile }
        : undefined;
    saveOrderMutation({
      payload,
      imageFiles,
    });
  };

  const handleCancel = () => {
    if (isDirty) {
      setIsDiscardModalOpen(true);
      return;
    }

    navigate(isAuthenticated ? routeEndpoints.orders : routeEndpoints.landing);
  };

  const isReadOnly = !!data?.trackingInfo?.trackingNumber;
  const shouldRedirect =
    isReadOnly || !!data?.orderLineItems?.[0]?.partInfo?.partNumber;

  useEffect(() => {
    if (!isLoaded || isAuthenticated) return;
    if (shouldRedirect && !location.state?.fromStatus) {
      navigate(`${routeEndpoints.shippingStatus}?id=${orderId}`, {
        replace: true,
      });
    }
  }, [
    isLoaded,
    isAuthenticated,
    shouldRedirect,
    location.state,
    navigate,
    orderId,
  ]);

  if (isLoading || !isLoaded) return <LoadingScreen />;

  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB] font-sans">
      <div className="max-w-200 w-full px-4 md:px-0 md:mx-auto">
        {isAuthenticated && (
          <Link
            to={routeEndpoints.orders}
            className="flex w-fit items-center gap-2 text-sm font-medium pt-7 text-blue-600 hover:text-blue-700 transition-colors"
          >
            <ArrowLeftIcon className="size-4" />
            Back to orders
          </Link>
        )}
        <div className="max-w-200 w-full border border-gray-200 rounded-xl mx-auto md:mb-20 mb-8 mt-8 md:p-5 px-4 py-6">
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <fieldset
                disabled={isReadOnly}
                className={
                  isReadOnly ? "opacity-50 pointer-events-none select-none" : ""
                }
              >
                <OrderDetails />
                <PartInformation />
                <ContactInformation />
              </fieldset>

              <div className="md:mt-12 mt-8 flex flex-col md:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="order-2 md:order-1 w-full h-13 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors"
                >
                  Cancel
                </button>

                {isReadOnly ? (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`${routeEndpoints.shippingStatus}?id=${orderId}`)
                    }
                    className="order-1 md:order-2 w-full h-13 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    Go to Status page
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isPending}
                    className="order-1 md:order-2 w-full h-13 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                  >
                    {isPending ? "Saving..." : "Update Order Details"}
                  </button>
                )}
              </div>

              <DiscardOrderModal
                isOpen={isDiscardModalOpen}
                onClose={() => setIsDiscardModalOpen(false)}
                onDiscard={() => {
                  navigate(
                    isAuthenticated
                      ? routeEndpoints.orders
                      : routeEndpoints.landing,
                  );
                }}
              />
            </form>
          </FormProvider>

          {orderId && (
            <>
              <ShippingStatusModal
                isOpen={isShippingModalOpen}
                orderId={orderId}
                onClose={() => setIsShippingModalOpen(false)}
                onSubmitted={() => {
                  setIsShippingModalOpen(false);
                  setIsSubmittedModalOpen(true);
                }}
              />
              <OrderSubmittedModal
                isOpen={isSubmittedModalOpen}
                orderId={orderId}
                trackingInfo={data?.trackingInfo}
                onClose={() => setIsSubmittedModalOpen(false)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
